import React, { useState, useEffect } from 'react';
import { PageWrapper, Filter } from '../../components';
import { Dropdown, Modal, Table, Pagination, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Pages } from '../../shared/enums';
import { useTranslation } from 'react-i18next';
import { getDriverTierList, deleteDriverTier, reorderDriverTiers } from '../../api/driver-tier';
import { RiAddLargeLine, RiDraggable } from 'react-icons/ri';
import Button from '../../components/common/Button';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const DriverTier = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
    const [totalPages, setTotalPage] = useState(0);
    const [data, setData] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTier, setSelectedTier] = useState(null);

    const onDragEnd = async (result) => {
        if (!result.destination) {
            return;
        }

        if (result.source.index === result.destination.index) {
            return;
        }

        const items = Array.from(data);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Optimistically update UI
        setData(items);

        // Create tierOrders array with new levels
        const tierOrders = items.map((tier, index) => ({
            id: tier.id,
            tierLevel: index + 1
        }));

        try {
            await reorderDriverTiers(tierOrders);
        } catch (error) {
            toast.error(error.message || t("FAILED_TO_REORDER_TIERS"));
            // Refresh to restore original order
            await getData(pagination);
        }
    };

    useEffect(() => {
        getData(pagination);
    }, [pagination]);

    const getData = async (pagination) => {
        const response = await getDriverTierList({
            skip: (pagination?.page - 1) * pagination?.pageSize,
            limit: pagination?.pageSize,
        });
        setData(response.items);
        setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize));
    };

    const handleDeleteClick = (tier) => {
        setSelectedTier(tier);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await deleteDriverTier(selectedTier.id);
            if (response.success) {
                setShowDeleteModal(false);
                setSelectedTier(null);
                getData(pagination);
            }
        } catch (error) {
            toast.error(error.message || t("FAILED_TO_DELETE_TIER"));
        }
    };

    const handlePageChange = (pageNumber) => {
        setPagination(prev => ({ ...prev, page: pageNumber }));
    };

    const handlePageSizeChange = (e) => {
        const newPageSize = parseInt(e.target.value);
        setPagination({ page: 1, pageSize: newPageSize });
    };

    return (
        <>
            <PageWrapper
                headerProps={{
                    title: "DRIVER_TIER",
                    rightElement: (
                        <Button
                            icon={<RiAddLargeLine />}
                            onClick={() => navigate(Pages.DriverTierDetail.replace(":id", "new"))}
                        >
                            {t("ADD")}
                        </Button>
                    )
                }}
            >

                <div className='w-100 overflow-auto mt-4' style={{ minHeight: "200px" }}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th className='text-center' style={{ width: '50px' }}></th>
                                    <th className='text-center'>{t("TIER_NAME")}</th>
                                    <th className='text-center'>{t("DRIVER_COMMISSION")}</th>
                                    <th className='text-center'>{t("COMPANY_COMMISSION")}</th>
                                    <th className='text-center'>{t("DESCRIPTION")}</th>
                                    <th className='text-center'>{t("ACTION")}</th>
                                </tr>
                            </thead>
                            <Droppable droppableId="tiers">
                                {(provided, snapshot) => (
                                    <tbody
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        style={{
                                            backgroundColor: snapshot.isDraggingOver ? '#f0f0f0' : 'transparent'
                                        }}
                                    >
                                        {data?.length > 0 ? (
                                            data?.map((tier, index) => (
                                                <Draggable
                                                    key={tier.id}
                                                    draggableId={tier.id}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => (
                                                        <tr
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            style={{
                                                                ...provided.draggableProps.style,
                                                                backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'transparent',
                                                                opacity: snapshot.isDragging ? 0.8 : 1,
                                                            }}
                                                        >
                                                            <td className='text-center' {...provided.dragHandleProps}>
                                                                <RiDraggable
                                                                    style={{
                                                                        fontSize: '18px',
                                                                        color: '#6c757d',
                                                                        cursor: 'grab'
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className='text-center'>{tier.tierName}</td>
                                                            <td className='text-center'>{tier.driverCommissionPercentage}%</td>
                                                            <td className='text-center'>{tier.companyCommissionPercentage}%</td>
                                                            <td className='text-center'>{tier.description || '-'}</td>
                                                            <td className='text-center'>
                                                                <Dropdown>
                                                                    <Dropdown.Toggle variant="primary" size="sm" className='py-0' />
                                                                    <Dropdown.Menu>
                                                                        <Dropdown.Item onClick={() => navigate(Pages.DriverTierDetail.replace(":id", tier.id))}>
                                                                            {t("EDIT")}
                                                                        </Dropdown.Item>
                                                                        <Dropdown.Item style={{ color: 'red' }} onClick={() => handleDeleteClick(tier)}>
                                                                            {t("DELETE")}
                                                                        </Dropdown.Item>
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </Draggable>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className='text-center py-5'>
                                                    {t("NO_DATA")}
                                                </td>
                                            </tr>
                                        )}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </div>

                <Row className="align-items-center flex-nowrap mt-3">
                    <Col xs={6} md={6}>
                        <Form.Group controlId="pageSize" className="d-flex align-items-center">
                            <Form.Label className="mb-0 mr-2">{t("ITEMS_PER_PAGE")}</Form.Label>
                            <Form.Control
                                as="select"
                                value={pagination.pageSize}
                                onChange={handlePageSizeChange}
                                style={{ width: '80px', display: 'inline-block', marginLeft: '10px' }}
                            >
                                {[25, 50, 100, 200].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                    <Col xs={6} md={6}>
                        <Pagination className="justify-content-end">
                            <Pagination.First
                                onClick={() => handlePageChange(1)}
                                disabled={pagination.page === 1}
                            />
                            <Pagination.Prev
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                            />
                            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(
                                (pageNumber) =>
                                    (pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        (pageNumber >= pagination.page - 2 && pageNumber <= pagination.page + 2)) && (
                                        <Pagination.Item
                                            key={pageNumber}
                                            active={pagination.page === pageNumber}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </Pagination.Item>
                                    )
                            )}
                            <Pagination.Next
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === totalPages}
                            />
                            <Pagination.Last
                                onClick={() => handlePageChange(totalPages)}
                                disabled={pagination.page === totalPages}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </PageWrapper>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("CONFIRM_DELETE")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t("DELETE_TIER_CONFIRMATION", { tierName: selectedTier?.tierName })}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        {t("CANCEL")}
                    </Button>
                    <Button variant="danger" onClick={handleDeleteConfirm}>
                        {t("DELETE")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DriverTier;



